import { Injectable } from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import {
    GroupMeetingCancellationNotice,
    GroupMeetingHostChangeNotice,
    MeetingsBookingsGroupMeeting,
    MeetingsBookingsMeetingItem,
    isGroupMeetingCancellationNotice,
    isGroupMeetingHostChangeNotice,
} from '@app/core/_models'
import { DialogService } from '@app/core/_services/dialog.service'
import { MeetingsService } from '@app/core/_services/meetings.service'
import { RealtimeService } from '@app/core/_services/realtime.service'
import { UtilsService } from '@app/core/_services/utils.service'
import {
    GROUP_MEETING_CANCELLATION_NOTICE_EVENT_TYPE,
    GROUP_MEETING_HOST_CHANGE_NOTICE_EVENT_TYPE,
    GroupMeetingCancellationNoticeEvent,
} from '@app/core/_services/realtime.types'
import type { GroupMeetingCancellationNoticeDialogData } from '@app/shared-dialogs/group-meeting-dialogs/group-meeting-cancellation-notice-dialog/group-meeting-cancellation-notice-dialog.component'
import type { GroupMeetingHostChangeItem } from '@app/shared-dialogs/group-meeting-dialogs/group-meeting-host-change-notice-dialog/group-meeting-host-change-notice-dialog.component'

const REALTIME_REFETCH_DEBOUNCE_MS = 250
const USER_BLOCK_NOTICE_DIALOG_DELAY_MS = 2000

/** What the session page tells this service about the group session it is on. */
export interface ActiveGroupMeetingSessionContext {
    /** True once the participant is connecting to or in the call. */
    isInCall: () => boolean
    /** A host-change notice is about to be shown before the join; hold the join. */
    onHostChangeGateOpened: () => void
    /**
     * True when Focus is already in progress and the participant has not yet
     * consented to the silent join; the notice then carries that information
     * so they see one modal, not two (D42).
     */
    needsLateJoinConsent: () => boolean
    /**
     * Called when the participant pressed the notice's Join, its only control,
     * and the notice was acknowledged; the page continues the join (D29).
     * lateJoinInfoShown says whether the silent-join information was included.
     */
    onHostChangeGateJoin: (lateJoinInfoShown: boolean) => void
}

interface HostChangeGroup {
    item: GroupMeetingHostChangeItem
    notices: GroupMeetingHostChangeNotice[]
}

function meetingHasFacilitator(meeting: MeetingsBookingsGroupMeeting | undefined): boolean {
    return !!meeting?.participantConfigs?.some((participant) => participant.role === 'facilitator')
}

@Injectable({
    providedIn: 'root',
})
export class GroupMeetingCancellationNoticeService {
    private readonly forceExitGroupSessionSubject = new Subject<number>()
    private readonly seenNoticeIds = new Set<number>()
    // Separate from seenNoticeIds: the two notice kinds come from different tables.
    private readonly seenHostChangeNoticeIds = new Set<number>()
    private bookingsSub: Subscription | null = null
    private realtimeSub: Subscription | null = null
    private realtimeRefetchTimeoutId: ReturnType<typeof setTimeout> | null = null
    private readonly noticeDialogTimeoutIds = new Set<ReturnType<typeof setTimeout>>()
    private activeGroupMeetingSessionTime: number | null = null
    private activeSessionContext: ActiveGroupMeetingSessionContext | null = null

    public readonly forceExitGroupSession$ = this.forceExitGroupSessionSubject.asObservable()

    constructor(
        private dialogService: DialogService,
        private meetingsService: MeetingsService,
        private realtimeService: RealtimeService,
        private utilsService: UtilsService,
    ) {}

    start(): void {
        if (!this.bookingsSub) {
            this.bookingsSub = this.meetingsService.meetingsBookingsSubject.subscribe((state) => {
                this.processNotices(state.groupMeetingCancellationNotices ?? [])
                this.processHostChangeNotices(
                    state.groupMeetingHostChangeNotices ?? [],
                    this.hasFacilitatorResolver(state.meetings ?? []),
                )
            })
        }

        if (!this.realtimeSub) {
            this.realtimeSub = this.realtimeService.onEvent.subscribe((event) => {
                if (event.type === GROUP_MEETING_CANCELLATION_NOTICE_EVENT_TYPE) {
                    this.handleRealtimeEvent(event)
                } else if (event.type === GROUP_MEETING_HOST_CHANGE_NOTICE_EVENT_TYPE) {
                    // The copy depends on the roster after the change, so the notice is
                    // shown from the refetched bookings rather than from the event.
                    this.scheduleRealtimeBookingsRefetch()
                }
            })
        }
    }

    stop(): void {
        this.bookingsSub?.unsubscribe()
        this.realtimeSub?.unsubscribe()
        this.bookingsSub = null
        this.realtimeSub = null
        this.seenNoticeIds.clear()
        this.seenHostChangeNoticeIds.clear()
        this.activeGroupMeetingSessionTime = null
        this.activeSessionContext = null
        if (this.realtimeRefetchTimeoutId !== null) {
            clearTimeout(this.realtimeRefetchTimeoutId)
            this.realtimeRefetchTimeoutId = null
        }
        for (const timeoutId of this.noticeDialogTimeoutIds) {
            clearTimeout(timeoutId)
        }
        this.noticeDialogTimeoutIds.clear()
    }

    processLaunchInfoNotices(notices: GroupMeetingCancellationNotice[] = []): boolean {
        return this.processNotices(notices)
    }

    /**
     * Host-change notices served with launch info. Returns true when the notice was
     * shown before the join, in which case the caller must not connect until the
     * participant presses the notice's Join (D29).
     */
    processLaunchInfoHostChangeNotices(
        notices: GroupMeetingHostChangeNotice[] = [],
        meeting: MeetingsBookingsGroupMeeting | null,
    ): boolean {
        return this.processHostChangeNotices(notices, (groupMeetingExternalId) =>
            meeting?.id === groupMeetingExternalId ? meetingHasFacilitator(meeting) : false,
        )
    }

    setActiveGroupMeetingSession(
        sessionTime: number,
        context: ActiveGroupMeetingSessionContext | null = null,
    ): void {
        if (!Number.isFinite(sessionTime) || sessionTime <= 0) {
            this.activeGroupMeetingSessionTime = null
            this.activeSessionContext = null
            return
        }

        this.activeGroupMeetingSessionTime = sessionTime
        this.activeSessionContext = context
    }

    clearActiveGroupMeetingSession(sessionTime?: number): void {
        if (
            sessionTime === undefined ||
            this.activeGroupMeetingSessionTime === null ||
            this.activeGroupMeetingSessionTime === sessionTime
        ) {
            this.activeGroupMeetingSessionTime = null
            this.activeSessionContext = null
        }
    }

    private handleRealtimeEvent(event: GroupMeetingCancellationNoticeEvent): void {
        this.processNotices([event.data])
        this.scheduleRealtimeBookingsRefetch()
    }

    private processNotices(notices: GroupMeetingCancellationNotice[]): boolean {
        const validNotices = notices
            .filter((notice) => isGroupMeetingCancellationNotice(notice))
            .filter((notice) => notice.reason !== 'facilitator_canceled_session')
        if (validNotices.length === 0) {
            return false
        }

        const hasCurrentSessionNotice = validNotices.some((notice) =>
            this.isNoticeForActiveGroupMeetingSession(notice),
        )

        if (hasCurrentSessionNotice && this.activeGroupMeetingSessionTime !== null) {
            this.forceExitGroupSessionSubject.next(this.activeGroupMeetingSessionTime)
        }

        const unseenNotices = validNotices.filter(
            (notice) => !this.seenNoticeIds.has(notice.noticeId),
        )
        if (unseenNotices.length === 0) {
            return hasCurrentSessionNotice
        }

        for (const notice of unseenNotices) {
            this.seenNoticeIds.add(notice.noticeId)
        }

        const dialogDelayMs = unseenNotices.some((notice) =>
            this.isUserBlockTriggeredNotice(notice),
        )
            ? USER_BLOCK_NOTICE_DIALOG_DELAY_MS
            : 0

        this.openCancellationNoticeDialog(
            {
                isInSession: hasCurrentSessionNotice,
                items: unseenNotices.map((notice) => ({
                    title: notice.title,
                    groupMeetingStartMs: notice.groupMeetingStartMs,
                    reason: notice.reason,
                })),
                currentSessionStartMs: hasCurrentSessionNotice
                    ? this.activeGroupMeetingSessionTime
                    : null,
            },
            () => this.acknowledgeNotices(unseenNotices),
            dialogDelayMs,
        )

        return hasCurrentSessionNotice
    }

    /**
     * One dialog per batch, one line per meeting (repeat notices for a meeting
     * collapse), every collapsed id acknowledged on Okay, never a force-exit.
     * Returns true when a notice for the active session was shown before the join.
     */
    private processHostChangeNotices(
        notices: GroupMeetingHostChangeNotice[],
        hasFacilitatorFor: (groupMeetingExternalId: string) => boolean,
    ): boolean {
        const unseenNotices = notices.filter(
            (notice) =>
                isGroupMeetingHostChangeNotice(notice) &&
                !this.seenHostChangeNoticeIds.has(notice.noticeId),
        )
        if (unseenNotices.length === 0) {
            return false
        }

        for (const notice of unseenNotices) {
            this.seenHostChangeNoticeIds.add(notice.noticeId)
        }

        const groups = this.groupHostChangeNotices(unseenNotices, hasFacilitatorFor)
        const activeGroup =
            this.activeGroupMeetingSessionTime === null
                ? null
                : (groups.find(
                      (group) =>
                          group.item.groupMeetingStartMs === this.activeGroupMeetingSessionTime,
                  ) ?? null)
        const otherGroups = groups.filter((group) => group !== activeGroup)

        if (otherGroups.length > 0) {
            const otherNotices = otherGroups.flatMap((group) => group.notices)
            this.dialogService.openGroupMeetingHostChangeNoticeDialog(
                { isInSession: false, items: otherGroups.map((group) => group.item) },
                () => this.acknowledgeHostChangeNotices(otherNotices),
            )
        }

        if (!activeGroup) {
            return false
        }

        const context = this.activeSessionContext
        if (context?.isInCall()) {
            this.dialogService.openGroupMeetingHostChangeNoticeDialog(
                { isInSession: true, items: [activeGroup.item] },
                () => this.acknowledgeHostChangeNotices(activeGroup.notices),
            )
            return false
        }

        // Not yet in the call: explain first; Join is the only control and continues the join.
        context?.onHostChangeGateOpened()
        const includesLateJoinInfo = context?.needsLateJoinConsent() ?? false
        this.dialogService.openGroupMeetingHostChangeNoticeDialog(
            {
                isInSession: false,
                items: [activeGroup.item],
                isPreJoinGate: true,
                includesLateJoinInfo,
            },
            () => {
                this.acknowledgeHostChangeNotices(activeGroup.notices)
                context?.onHostChangeGateJoin(includesLateJoinInfo)
            },
        )
        return true
    }

    private groupHostChangeNotices(
        notices: GroupMeetingHostChangeNotice[],
        hasFacilitatorFor: (groupMeetingExternalId: string) => boolean,
    ): HostChangeGroup[] {
        const groupsByMeeting = new Map<string, HostChangeGroup>()
        // Ascending so the latest notice for a meeting decides its reason.
        const sortedNotices = [...notices].sort((a, b) => a.noticeId - b.noticeId)
        for (const notice of sortedNotices) {
            const existing = groupsByMeeting.get(notice.groupMeetingExternalId)
            if (existing) {
                existing.notices.push(notice)
                existing.item.reason = notice.reason
                continue
            }
            groupsByMeeting.set(notice.groupMeetingExternalId, {
                item: {
                    title: notice.title,
                    groupMeetingStartMs: notice.groupMeetingStartMs,
                    reason: notice.reason,
                    hasFacilitator: hasFacilitatorFor(notice.groupMeetingExternalId),
                },
                notices: [notice],
            })
        }
        return [...groupsByMeeting.values()].sort(
            (a, b) => a.item.groupMeetingStartMs - b.item.groupMeetingStartMs,
        )
    }

    private hasFacilitatorResolver(
        meetings: MeetingsBookingsMeetingItem[],
    ): (groupMeetingExternalId: string) => boolean {
        return (groupMeetingExternalId) =>
            meetingHasFacilitator(
                meetings.find(
                    (meeting): meeting is MeetingsBookingsGroupMeeting =>
                        meeting.meetingType === 'group_meeting' &&
                        meeting.id === groupMeetingExternalId,
                ),
            )
    }

    private acknowledgeHostChangeNotices(notices: GroupMeetingHostChangeNotice[]): void {
        for (const notice of notices) {
            this.meetingsService
                .acknowledgeHostChangeNotice({ noticeId: notice.noticeId })
                .subscribe({
                    error: () => {
                        this.seenHostChangeNoticeIds.delete(notice.noticeId)
                    },
                })
        }
    }

    private scheduleRealtimeBookingsRefetch(): void {
        if (this.realtimeRefetchTimeoutId !== null) {
            clearTimeout(this.realtimeRefetchTimeoutId)
        }

        this.realtimeRefetchTimeoutId = setTimeout(() => {
            this.realtimeRefetchTimeoutId = null
            this.meetingsService.getMeetingsBookings().subscribe({
                error: () => {},
            })
        }, REALTIME_REFETCH_DEBOUNCE_MS)
    }

    private acknowledgeNotices(notices: GroupMeetingCancellationNotice[]): void {
        for (const notice of notices) {
            this.meetingsService
                .acknowledgeCancellationNotice({ noticeId: notice.noticeId })
                .subscribe({
                    error: () => {
                        this.seenNoticeIds.delete(notice.noticeId)
                    },
                })
        }
    }

    private openCancellationNoticeDialog(
        data: GroupMeetingCancellationNoticeDialogData,
        afterClosed: () => void,
        delayMs: number,
    ): void {
        if (delayMs <= 0) {
            this.dialogService.openGroupMeetingCancellationNoticeDialog(data, afterClosed)
            return
        }

        // Block flows show a confirmation dialog; delay so this notice does not appear first.
        const timeoutId = setTimeout(() => {
            this.noticeDialogTimeoutIds.delete(timeoutId)
            this.dialogService.openGroupMeetingCancellationNoticeDialog(data, afterClosed)
        }, delayMs)
        this.noticeDialogTimeoutIds.add(timeoutId)
    }

    private isNoticeForActiveGroupMeetingSession(notice: GroupMeetingCancellationNotice): boolean {
        return this.activeGroupMeetingSessionTime === notice.groupMeetingStartMs
    }

    private isUserBlockTriggeredNotice(notice: GroupMeetingCancellationNotice): boolean {
        return (
            notice.reason === 'user_blocked_participant' ||
            notice.reason === 'user_blocked_facilitator'
        )
    }
}
